import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { BarChart3, Wrench, ExternalLink } from "lucide-react";
import { relatorioAjustes } from "../services/ajustes";
import { listarUsuariosAtivos } from "../services/usuarios";
import { Avatar } from "../components/Avatar";

function primeiroDiaMes() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
}
function hojeISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function fmtDuracao(ms: number) {
  const totalMin = Math.round(ms / 60000);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  if (h === 0) return `${m}min`;
  return `${h}h${String(m).padStart(2, "0")}`;
}

export function RelatorioAjustes() {
  const [inicio, setInicio] = useState(primeiroDiaMes());
  const [fim, setFim] = useState(hojeISO());
  const [usuarioId, setUsuarioId] = useState("");

  const { data: usuarios = [] } = useQuery({ queryKey: ["usuarios-ativos"], queryFn: listarUsuariosAtivos });

  // Converte pra ISO com hora (início 00:00, fim 23:59:59 do dia).
  const inicioISO = `${inicio}T00:00:00`;
  const fimISO = `${fim}T23:59:59`;

  const { data, isLoading, isError } = useQuery({
    queryKey: ["relatorio-ajustes", inicio, fim, usuarioId],
    queryFn: () => relatorioAjustes({ inicio: inicioISO, fim: fimISO, usuarioId: usuarioId || undefined }),
    enabled: !!inicio && !!fim,
  });
  const geral = data?.geral;
  const relatorio = data?.usuarios ?? [];

  const corCategoria = (cat: string) => (cat === "cliente" ? "bg-amber-500" : "bg-blue-500");
  // Cor do "peso" de ajustes sobre o trabalho: verde ok, âmbar atenção, vermelho alto.
  const corPeso = (pct: number) => (pct >= 40 ? "bg-red-500" : pct >= 20 ? "bg-amber-500" : "bg-emerald-500");

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-bold text-slate-800 dark:text-slate-100 flex items-center gap-2">
          <BarChart3 size={20} /> Relatório de ajustes
        </h1>
        <p className="text-sm text-slate-500 dark:text-slate-400">
          Quantos <strong>ajustes</strong> cada pessoa teve — por motivo e categoria (interno × cliente) — e quanto tempo foi gasto refazendo.
          A <strong>quantidade</strong> conta todos os ajustes do período, mesmo os que ninguém cronometrou: um card que voltou 10 vezes
          pesa, mesmo que tenha dado pouco tempo. Já o <strong>tempo</strong> só aparece quando alguém deu "Estou nessa" com a tarefa na
          coluna <strong>Ajustes</strong> — por isso costuma ser bem menor que a realidade.
        </p>
      </div>

      <div className="flex flex-wrap gap-3 items-end bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl p-4">
        <div>
          <label className="text-xs text-slate-500 block mb-1">De</label>
          <input type="date" value={inicio} onChange={(e) => setInicio(e.target.value)} className="px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 dark:bg-slate-800 text-sm outline-none" />
        </div>
        <div>
          <label className="text-xs text-slate-500 block mb-1">Até</label>
          <input type="date" value={fim} onChange={(e) => setFim(e.target.value)} className="px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 dark:bg-slate-800 text-sm outline-none" />
        </div>
        <div>
          <label className="text-xs text-slate-500 block mb-1">Pessoa</label>
          <select value={usuarioId} onChange={(e) => setUsuarioId(e.target.value)} className="px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 dark:bg-slate-800 text-sm outline-none">
            <option value="">Todas</option>
            {usuarios.map((u: any) => <option key={u.id} value={u.id}>{u.nome}</option>)}
          </select>
        </div>
      </div>

      {/* GERAL da Rec (ou da pessoa filtrada) — trabalhado × ajustes (melhoria 23) */}
      {geral && (geral.totalTrabalhadoMs > 0 || geral.qtdAjustes > 0) && (
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl p-4">
          <div className="flex flex-wrap items-baseline justify-between gap-2 mb-3">
            <p className="font-semibold text-slate-800 dark:text-slate-100">
              {usuarioId ? "Geral da pessoa" : "Geral da Rec"} <span className="text-xs font-normal text-slate-400">no período</span>
            </p>
            <p className="text-sm text-slate-500">
              <span className="font-semibold text-slate-700 dark:text-slate-200">{fmtDuracao(geral.totalTrabalhadoMs)}</span> trabalhados ·{" "}
              <span className="font-semibold text-slate-700 dark:text-slate-200">{fmtDuracao(geral.totalAjustesMs)}</span> em ajustes
            </p>
          </div>

          {/* QUANTIDADE em destaque — é o número que sobrevive à falta de play. */}
          <div className="flex items-baseline gap-2 mb-3">
            <span className="text-4xl font-bold text-slate-800 dark:text-slate-100 tabular-nums leading-none">{geral.qtdAjustes}</span>
            <span className="text-sm text-slate-500">{geral.qtdAjustes === 1 ? "ajuste no período" : "ajustes no período"}</span>
          </div>
          {/* O tempo continua aqui embaixo, menor — vale quando existe, mas hoje
              cobre pouco do que aconteceu (a maioria dos ajustes não tem play). */}
          {geral.totalTrabalhadoMs > 0 && (
            <>
              <div className="flex h-3 rounded-full overflow-hidden bg-slate-100 dark:bg-slate-800" title={`${geral.percentual}% do tempo trabalhado foi em ajustes`}>
                <div className={corPeso(geral.percentual)} style={{ width: `${Math.min(100, geral.percentual)}%` }} />
              </div>
              <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
                <span className={`font-bold text-lg ${geral.percentual >= 40 ? "text-red-600" : geral.percentual >= 20 ? "text-amber-600" : "text-emerald-600"}`}>{geral.percentual}%</span> do tempo trabalhado foi em ajustes.
              </p>
            </>
          )}
        </div>
      )}

      <div className="flex items-center gap-4 text-xs text-slate-500">
        <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-blue-500" /> Ajuste interno</span>
        <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-amber-500" /> Ajuste do cliente</span>
      </div>

      {isLoading && <p className="text-slate-400 text-sm">Carregando…</p>}
      {isError && <p className="text-red-500 text-sm">Erro ao carregar relatório.</p>}
      {!isLoading && !isError && relatorio.length === 0 && (
        <div className="text-center py-12 text-slate-400">
          <Wrench size={32} className="mx-auto mb-2 opacity-40" />
          Nenhum ajuste registrado no período.
        </div>
      )}

      <div className="space-y-3">
        {relatorio.map((u) => (
          <div key={u.usuario.id} className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl p-4">
            <div className="flex items-center gap-3 mb-3">
              <Avatar nome={u.usuario.nome} fotoUrl={u.usuario.fotoUrl} tamanho={32} />
              <div className="flex-1">
                <p className="font-semibold text-slate-800 dark:text-slate-100">{u.usuario.nome}</p>
                <p className="text-xs text-slate-500">
                  {fmtDuracao(u.totalTrabalhadoMs)} trabalhados · {fmtDuracao(u.totalUtilMs)} em ajustes
                  <span className={`ml-1 font-semibold ${u.percentualAjuste >= 40 ? "text-red-600" : u.percentualAjuste >= 20 ? "text-amber-600" : "text-emerald-600"}`}>({u.percentualAjuste}%)</span>
                </p>
              </div>
              {/* Número principal da pessoa: quantos ajustes, não quanto tempo. */}
              <div className="text-right shrink-0" title="Ajustes no período (todos, com ou sem cronômetro)">
                <p className="text-2xl font-bold text-slate-800 dark:text-slate-100 tabular-nums leading-none">{u.qtdAjustes}</p>
                <p className="text-[11px] text-slate-400">{u.qtdAjustes === 1 ? "ajuste" : "ajustes"}</p>
              </div>
            </div>

            {/* Barra 1: parcela do TRABALHO que foi ajuste (melhoria 23) */}
            <div className="mb-3">
              <div className="flex h-2.5 rounded-full overflow-hidden bg-slate-100 dark:bg-slate-800" title={`${u.percentualAjuste}% do tempo trabalhado foi em ajustes`}>
                <div className={corPeso(u.percentualAjuste)} style={{ width: `${Math.min(100, u.percentualAjuste)}%` }} />
              </div>
              <p className="text-[11px] text-slate-400 mt-1">Proporção do tempo trabalhado gasta em ajustes</p>
            </div>

            {/* Barra 2: composição dos ajustes por motivo (interno × cliente) */}
            <p className="text-[11px] text-slate-400 mb-1">Ajustes por motivo (quantidade)</p>
            <div className="flex h-2.5 rounded-full overflow-hidden mb-3 bg-slate-100 dark:bg-slate-800">
              {u.porMotivo.map((m, i) => (
                <div
                  key={i}
                  className={corCategoria(m.categoria)}
                  style={{ width: `${u.qtdAjustes > 0 ? (m.quantidade / u.qtdAjustes) * 100 : m.percentual}%` }}
                  title={`${m.motivoLabel} — ${m.quantidade} ${m.quantidade === 1 ? "ajuste" : "ajustes"}`}
                />
              ))}
            </div>
            <div className="space-y-1.5">
              {u.porMotivo.map((m, i) => (
                <div key={i} className="flex items-center gap-2 text-sm">
                  <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${corCategoria(m.categoria)}`} />
                  <span className="flex-1 text-slate-700 dark:text-slate-200">
                    {m.motivoLabel}
                    <span className="text-xs text-slate-400 ml-1">({m.categoria === "cliente" ? "cliente" : "interno"})</span>
                  </span>
                  <span className="text-slate-400 tabular-nums text-xs w-16 text-right" title="Tempo cronometrado neste motivo">
                    {m.totalUtilMs > 0 ? fmtDuracao(m.totalUtilMs) : "—"}
                  </span>
                  <span className="font-semibold text-slate-700 dark:text-slate-200 tabular-nums w-16 text-right">
                    {m.quantidade} {m.quantidade === 1 ? "ajuste" : "ajustes"}
                  </span>
                </div>
              ))}
            </div>

            {/* Tarefas com ajuste — clicáveis pra abrir o card (melhoria 23) */}
            {u.tarefas.length > 0 && (
              <div className="mt-3 pt-3 border-t border-slate-100 dark:border-slate-800">
                <p className="text-[11px] text-slate-400 mb-1.5">Tarefas com ajuste (clique pra abrir)</p>
                <div className="space-y-1">
                  {u.tarefas.map((t) => (
                    <Link
                      key={t.tarefaId}
                      to={`/tarefas?tarefa=${t.tarefaId}`}
                      className="flex items-center gap-2 text-sm px-2 py-1 -mx-2 rounded hover:bg-slate-50 dark:hover:bg-slate-800 group"
                    >
                      <ExternalLink size={13} className="text-slate-400 group-hover:text-blue-600 shrink-0" />
                      <span className="flex-1 truncate text-slate-700 dark:text-slate-200 group-hover:text-blue-600">
                        {t.titulo}
                        {t.projetoNome && <span className="text-xs text-slate-400 ml-1">· {t.projetoNome}</span>}
                      </span>
                      {/* "voltou N vezes" — o sinal que a Thainá quer ver: um card
                          com 10 ajustes é grave mesmo com pouco tempo cronometrado. */}
                      <span className="text-slate-400 tabular-nums text-[11px] shrink-0 w-14 text-right">
                        {t.totalUtilMs > 0 ? fmtDuracao(t.totalUtilMs) : "—"}
                      </span>
                      <span
                        className={`tabular-nums text-xs font-semibold shrink-0 w-20 text-right ${t.qtdAjustes >= 5 ? "text-red-600" : t.qtdAjustes >= 3 ? "text-amber-600" : "text-slate-600 dark:text-slate-300"}`}
                        title={`Voltou ${t.qtdAjustes}× para ajustes no período`}
                      >
                        {t.qtdAjustes}× ajuste{t.qtdAjustes === 1 ? "" : "s"}
                      </span>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
